import { RevenueAccounts } from './revenue-accounts.js';
import {
  ACCOUNTING_GROUPS,
  SUGGESTED_CHART,
  accountGroupOf,
  accountLevelOf,
  accountParentOf,
  validateAccountCode,
  type AccountingGroup,
} from '@hue/contracts';
import {
  Button,
  Card,
  CardHeader,
  Input,
  SegmentedControl,
  StatusPill,
  useSnackbar,
  IconButton,
} from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FolderTree, ListTree, Plus, PencilLine, Archive, RotateCcw } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { ListFilterBar, ErrorCard, LoadingCard, RequestAlert } from '../../app/data-view.js';
import { PageHeader } from '../../app/page-title.js';
import { getApiError, getApiErrorField } from '../../lib/api.js';
import { accountingApi, type AccountingAccount } from './api.js';
import { nextCodeUnder } from './chart-codes.js';

const ACCOUNTS_KEY = ['company', 'accounting', 'accounts'] as const;

/// Sem acento nem caixa: "agua" encontra "Água".
const plain = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const byCode = (left: { code: string }, right: { code: string }) =>
  left.code.localeCompare(right.code, 'pt-BR', { numeric: true });

type AddDraft = { parent: string; code: string; name: string };
type FieldErrors = {
  code?: string | undefined;
  name?: string | undefined;
  form?: string | undefined;
};

export function ChartOfAccounts({
  canManage,
  salesContracted = false,
}: {
  canManage: boolean;
  salesContracted?: boolean;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const query = useQuery({ queryKey: ACCOUNTS_KEY, queryFn: accountingApi.accounts });
  const [filter, setFilter] = useState<'active' | 'all'>('active');
  const [search, setSearch] = useState('');
  const [groupsOnly, setGroupsOnly] = useState(false);
  const [adding, setAdding] = useState<AddDraft | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [busy, setBusy] = useState(false);

  const accounts = query.data ?? [];

  async function refresh(next?: AccountingAccount[]) {
    if (next) queryClient.setQueryData(ACCOUNTS_KEY, next);
    await queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY });
  }

  async function run(action: () => Promise<void>) {
    setBusy(true);
    try {
      await action();
    } catch (error) {
      // Recusa de ação sem campo para corrigir: aviso flutuante embaixo, sem empurrar a lista.
      snackbar.failure(getApiError(error));
    } finally {
      setBusy(false);
    }
  }

  function startAdding(parent: string) {
    setRenaming(null);
    setErrors({});
    setAdding({ parent, code: nextCodeUnder(parent, accounts), name: '' });
  }

  async function submitAdd(event: FormEvent) {
    event.preventDefault();
    if (!adding) return;
    const code = adding.code.trim();
    const name = adding.name.trim();
    const local: FieldErrors = {
      code:
        validateAccountCode(code) ??
        (accountParentOf(code) === adding.parent
          ? undefined
          : `O código precisa começar por ${adding.parent}.`),
      name: name.length < 2 ? 'Dê à conta um nome de pelo menos 2 letras.' : undefined,
    };
    if (local.code ?? local.name) {
      setErrors(local);
      return;
    }
    setBusy(true);
    try {
      const account = await accountingApi.create({ code, name });
      setAdding(null);
      setErrors({});
      await refresh();
      snackbar.success(`Conta ${account.code} ${account.name} criada.`);
    } catch (error) {
      const field = getApiErrorField(error);
      setErrors(
        field === 'code' || field === 'name'
          ? { [field]: getApiError(error) }
          : { form: getApiError(error) },
      );
    } finally {
      setBusy(false);
    }
  }

  async function submitRename(event: FormEvent) {
    event.preventDefault();
    if (!renaming) return;
    if (renaming.name.trim().length < 2) {
      setErrors({ name: 'Dê à conta um nome de pelo menos 2 letras.' });
      return;
    }
    setBusy(true);
    try {
      await accountingApi.rename(renaming.id, renaming.name);
      setRenaming(null);
      setErrors({});
      await refresh();
      snackbar.success('Conta renomeada. O que já foi lançado passa a mostrar o nome novo.');
    } catch (error) {
      setErrors({ name: getApiError(error) });
    } finally {
      setBusy(false);
    }
  }

  const toggle = (account: AccountingAccount) =>
    run(async () => {
      await accountingApi.setActive(account.id, !account.active);
      await refresh();
      snackbar.success(
        account.active
          ? 'Conta desativada. Ela some das escolhas e continua no que já foi lançado.'
          : 'Conta reativada.',
      );
    });

  const adopt = () =>
    run(async () => {
      const next = await accountingApi.adoptSuggested();
      await refresh(next);
      snackbar.success('Plano sugerido adotado. Ajuste o que precisar.');
    });

  const header = (
    <PageHeader
      trail="Contabilidade gerencial / Plano de contas"
      title="Plano de contas"
      description="A lista de onde cada receita e cada despesa entra. O DRE e o fluxo de caixa por conta somam por ela. Vale para todas as unidades da empresa."
    />
  );

  if (query.isPending) {
    return (
      <section className="min-w-0">
        {header}
        <div className="grid min-w-0 gap-6">
          <LoadingCard label="Carregando o plano de contas" />
        </div>
      </section>
    );
  }
  if (query.isError) {
    return (
      <section className="min-w-0">
        {header}
        <div className="grid min-w-0 gap-6">
          <ErrorCard message={getApiError(query.error)} />
        </div>
      </section>
    );
  }

  if (accounts.length === 0 && !groupsOnly) {
    return (
      <section className="min-w-0">
        {header}
        <div className="grid min-w-0 gap-6">
          <Card className="grid justify-items-center gap-4 text-center">
            <FolderTree className="size-8 text-brand-500" aria-hidden="true" />
            <h2 className="text-lg font-semibold">Monte o plano de contas da empresa</h2>
            <p className="max-w-2xl text-sm text-content-muted">
              Comece pelo plano sugerido para clínicas de estética e ajuste depois, ou comece só com
              os seis grupos e crie as contas uma a uma. O plano sugerido tem{' '}
              {SUGGESTED_CHART.length} contas. Nada é lançado nem classificado ao adotá-lo.
            </p>
            {canManage ? (
              <div className="flex flex-wrap justify-center gap-2">
                <Button onClick={() => void adopt()} disabled={busy}>
                  Usar o plano sugerido
                </Button>
                <Button variant="outline" onClick={() => setGroupsOnly(true)}>
                  Começar só com os grupos
                </Button>
              </div>
            ) : (
              <p className="text-sm text-content-muted">
                Peça a quem gerencia o plano de contas para montá-lo.
              </p>
            )}
          </Card>
        </div>
      </section>
    );
  }

  const needle = plain(search.trim());
  const visible = (account: AccountingAccount) =>
    (filter === 'all' || account.active) &&
    (!needle || plain(`${account.code} ${account.name}`).includes(needle));
  const hasChildren = (code: string) => accounts.some((a) => accountParentOf(a.code) === code);
  const activeCount = accounts.filter((account) => account.active).length;
  const shownCount = accounts.filter(visible).length;

  const addForm = (parent: string) =>
    adding?.parent === parent ? (
      <form
        onSubmit={(event) => void submitAdd(event)}
        className="grid gap-3 border-t border-surface-border bg-surface px-4 py-3 sm:grid-cols-[8rem_minmax(0,1fr)_auto]"
        aria-label={`Nova conta em ${parent}`}
      >
        <Input
          label="Código"
          value={adding.code}
          error={errors.code}
          onChange={(event) => setAdding({ ...adding, code: event.target.value })}
        />
        <Input
          label="Nome da conta"
          value={adding.name}
          error={errors.name}
          autoFocus
          onChange={(event) => setAdding({ ...adding, name: event.target.value })}
        />
        <div className="flex items-end gap-2">
          <Button type="submit" size="sm" disabled={busy}>
            Adicionar
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setAdding(null)}>
            Cancelar
          </Button>
        </div>
        {errors.form ? (
          <div className="sm:col-span-3">
            <RequestAlert message={errors.form} />
          </div>
        ) : null}
      </form>
    ) : null;

  const row = (account: AccountingAccount) => {
    const level = accountLevelOf(account.code);
    const leaf = !hasChildren(account.code);
    if (renaming?.id === account.id) {
      return (
        <form
          key={account.id}
          onSubmit={(event) => void submitRename(event)}
          className="grid gap-3 border-t border-surface-border bg-surface px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto]"
          aria-label={`Renomear ${account.code}`}
        >
          <Input
            label={`Novo nome de ${account.code}`}
            value={renaming.name}
            error={errors.name}
            autoFocus
            onChange={(event) => setRenaming({ ...renaming, name: event.target.value })}
          />
          <div className="flex items-end gap-2">
            <Button type="submit" size="sm" disabled={busy}>
              Salvar
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setRenaming(null)}>
              Cancelar
            </Button>
          </div>
        </form>
      );
    }
    return (
      <div key={account.id}>
        <div
          className={`flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-surface-border py-2 pr-4 text-sm ${
            level === 3 ? 'pl-6 sm:pl-10' : 'pl-4'
          } ${account.active ? '' : 'opacity-60'}`}
        >
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <span className="w-12 shrink-0 font-semibold tabular-nums text-content-muted">
              {account.code}
            </span>
            <span className={`min-w-0 break-words ${account.active ? '' : 'line-through'}`}>
              {account.name}
            </span>
            {account.active ? null : <StatusPill tone="neutral">Desativada</StatusPill>}
            {leaf ? null : <span className="text-xs text-content-muted">agrupa</span>}
            {leaf && account.usageCount > 0 ? (
              <span className="text-xs text-content-muted">
                em uso em {account.usageCount}{' '}
                {account.usageCount === 1 ? 'referência' : 'referências'}
                {level === 2 ? ' · não ganha subconta' : ''}
              </span>
            ) : null}
          </div>
          {canManage ? (
            <div className="flex flex-wrap gap-1">
              {level === 2 && account.active && !(leaf && account.usageCount > 0) ? (
                <IconButton
                  label="+ Subconta"
                  icon={<Plus />}
                  onClick={() => startAdding(account.code)}
                />
              ) : null}
              {account.active ? (
                <IconButton
                  label="Renomear"
                  icon={<PencilLine />}
                  onClick={() => {
                    setAdding(null);
                    setErrors({});
                    setRenaming({ id: account.id, name: account.name });
                  }}
                />
              ) : null}
              <IconButton
                label={account.active ? 'Desativar' : 'Reativar'}
                icon={account.active ? <Archive /> : <RotateCcw />}
                tone={account.active ? 'danger' : 'neutral'}
                disabled={busy}
                onClick={() => void toggle(account)}
              />
            </div>
          ) : null}
        </div>
        {addForm(account.code)}
      </div>
    );
  };

  const groupCard = (group: AccountingGroup) => {
    const inGroup = accounts
      .filter((account) => accountGroupOf(account.code)?.code === group.code)
      .sort(byCode);
    const shown = inGroup.filter(visible);
    if (needle && shown.length === 0 && adding?.parent !== group.code) return null;
    return (
      <Card key={group.code} className="overflow-hidden p-0">
        <div className="px-4 pt-4">
          <CardHeader
            title={`${group.code} ${group.name}`}
            icon={<ListTree className="size-4" />}
            action={
              canManage ? (
                <IconButton label="Conta" icon={<Plus />} onClick={() => startAdding(group.code)} />
              ) : undefined
            }
          />
          <p className="-mt-2 mb-3 text-xs text-content-muted">
            {group.dreLine
              ? `No DRE: ${group.dreLine}`
              : 'Não entra no DRE: aportes, retiradas e transferências.'}
          </p>
        </div>
        {shown.map(row)}
        {addForm(group.code)}
        {shown.length === 0 && adding?.parent !== group.code ? (
          <p className="border-t border-surface-border px-4 py-3 text-sm text-content-muted">
            {inGroup.length ? 'Nenhuma conta ativa neste grupo.' : 'Nenhuma conta neste grupo.'}
          </p>
        ) : null}
      </Card>
    );
  };

  const cards = ACCOUNTING_GROUPS.map(groupCard).filter(Boolean);

  return (
    <section className="min-w-0">
      {header}
      <ListFilterBar
        showViewPicker={false}
        searchLabel="Buscar conta"
        searchPlaceholder="Buscar por nome ou código"
        search={search}
        onSearchChange={setSearch}
        count={
          <>
            {activeCount} {activeCount === 1 ? 'conta ativa' : 'contas ativas'}
            {needle || filter === 'all' ? ` · ${shownCount} na lista` : ''}
          </>
        }
      >
        <SegmentedControl
          label="Quais contas mostrar"
          compact
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'active', label: 'Ativas' },
            { value: 'all', label: 'Todas' },
          ]}
        />
      </ListFilterBar>
      <div className="grid min-w-0 gap-6">
        <p className="text-sm text-content-muted">
          Os seis grupos são fixos, porque o DRE depende deles. Só contas sem subcontas recebem
          lançamentos: “Pessoal” agrupa, “Salários” recebe.
        </p>
        {salesContracted ? <RevenueAccounts accounts={accounts} canManage={canManage} /> : null}
        {cards.length ? (
          <div className="grid gap-4">{cards}</div>
        ) : (
          <Card className="text-center text-sm text-content-muted">
            Nenhuma conta com “{search.trim()}”.
          </Card>
        )}
      </div>
    </section>
  );
}
